import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { eur, pct, num, dateIt, dateTimeIt, OCC_LABELS, STATE_COLORS, scoreColor, PRIORITY } from "@/lib/format";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft, Loader2, Sparkles, Upload, CheckCircle2, XCircle, MapPin, Building2,
  FileText, Phone, Mail, ChevronDown, TrendingUp, ExternalLink, Download, AlertTriangle, Plus, ClipboardCheck,
} from "lucide-react";
import { DueDiligencePanel } from "@/components/DueDiligencePanel";
import { DemoBadge } from "@/components/DemoBadge";
import { useAuth } from "@/context/AuthContext";

const WEIGHTS = { prezzo: 20, margine: 20, occupazione: 15, liberazione: 10, regolarita: 15, liquidabilita: 10, comune: 5, domanda: 5 };
const CRIT_LABELS = { prezzo: "Prezzo", margine: "Margine", occupazione: "Occupazione", liberazione: "Liberazione", regolarita: "Regolarità", liquidabilita: "Liquidabilità", comune: "Comune", domanda: "Domanda" };
const WORKFLOW = ["Nuova", "In Analisi", "Qualificata", "Contatto da effettuare", "Contatto effettuato", "Documentazione richiesta", "In trattativa", "Due diligence", "Deliberata", "Acquistata", "Scartata"];

function Section({ title, children, action, testid }) {
  return (
    <div className="bg-[#122A3F] border border-white/10 rounded-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
        <h2 className="text-sm font-medium text-zinc-200 uppercase tracking-wider">{title}</h2>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const AFF = {
  certo: { c: "#10B981", t: "Dato certo" },
  dedotto: { c: "#D97706", t: "Dato dedotto" },
  da_verificare: { c: "#E11D48", t: "Da verificare manualmente" },
};

const confColor = (v) => (v == null ? "#71717A" : v >= 70 ? "#10B981" : v >= 45 ? "#EA7B2B" : "#E11D48");

const CC_STATUS = {
  confermato: { c: "#10B981", label: "Concorde" },
  discrepanza: { c: "#E11D48", label: "Discrepanza" },
  singola_fonte: { c: "#D97706", label: "Fonte unica" },
};

const TL_COLORS = {
  acquisizione: "#3B82F6", documento: "#8B5CF6", ai: "#EA7B2B", stato: "#10B981",
  prezzo: "#E11D48", data: "#F59E0B", variazione: "#0EA5E9", contatto: "#22C55E", nota: "#a1a1aa",
};
const TL_LABELS = {
  acquisizione: "Acquisizione", documento: "Documento", ai: "Analisi AI", stato: "Cambio stato",
  prezzo: "Variazione prezzo", data: "Cambio data asta", variazione: "Variazione", contatto: "Contatto", nota: "Nota",
};

function CrossCheck({ cc, cols = "Portale · Documenti" }) {
  if (!cc || !(cc.fields || []).length) return null;
  return (
    <div className="space-y-1.5" data-testid="crosscheck-table">
      <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 pb-1.5 border-b border-white/10 text-[10px] uppercase tracking-wider text-zinc-600">
        <span>Campo</span><span className="text-right">{cols}</span><span className="text-right">Esito</span>
      </div>
      {cc.fields.map((f, i) => {
        const s = CC_STATUS[f.stato] || CC_STATUS.singola_fonte;
        return (
          <div key={i} className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-center py-1 border-b border-white/5 last:border-0" data-testid={`crosscheck-row-${f.campo}`}>
            <span className="text-sm text-zinc-400">{f.label}</span>
            <span className="text-right text-xs font-mono text-zinc-300">
              <span className="text-[#EA7B2B]">{f.portale ?? "—"}</span>
              <span className="text-zinc-600"> / </span>
              <span className="text-zinc-400">{f.documenti ?? "—"}</span>
            </span>
            <span className="text-right text-[10px] uppercase tracking-wider font-medium justify-self-end px-1.5 py-0.5 rounded-sm" style={{ color: s.c, backgroundColor: `${s.c}1a` }}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ProvDot({ prov }) {
  if (!prov) return null;
  const a = AFF[prov.affidabilita] || AFF.dedotto;
  return (
    <span
      title={`${a.t} · Fonte: ${prov.fonte}`}
      data-testid="prov-dot"
      className="inline-block w-2 h-2 rounded-full ml-2 align-middle shrink-0"
      style={{ backgroundColor: a.c }}
    />
  );
}

function Row({ label, value, mono = true, accent, prov }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <span className={`text-sm flex items-center ${mono ? "font-mono" : ""}`} style={{ color: accent || "#e4e4e7" }}>
        {value}
        <ProvDot prov={prov} />
      </span>
    </div>
  );
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [operatori, setOperatori] = useState([]);
  const [newAtt, setNewAtt] = useState({ testo: "", scadenza: "" });
  const [newNota, setNewNota] = useState("");
  const fileRef = useRef();

  const load = () => api.get(`/deals/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Operazione non trovata"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/operatori").then((r) => setOperatori(r.data)).catch(() => {}); }, []);

  const saveCrm = async (patch) => {
    try { const { data } = await api.patch(`/deals/${id}/crm`, patch); setD(data); toast.success("CRM aggiornato"); }
    catch { toast.error("Errore aggiornamento CRM"); }
  };
  const addAttivita = async () => {
    if (!newAtt.testo.trim()) return;
    try { const { data } = await api.post(`/deals/${id}/crm/attivita`, { testo: newAtt.testo, scadenza: newAtt.scadenza || null }); setD(data); setNewAtt({ testo: "", scadenza: "" }); }
    catch { toast.error("Errore attività"); }
  };
  const toggleAttivita = async (aid) => {
    try { const { data } = await api.patch(`/deals/${id}/crm/attivita/${aid}`); setD(data); } catch { /* noop */ }
  };
  const addNota = async () => {
    if (!newNota.trim()) return;
    try { const { data } = await api.post(`/deals/${id}/crm/note`, { testo: newNota }); setD(data); setNewNota(""); }
    catch { toast.error("Errore nota"); }
  };

  if (!d)
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>;

  const fin = d.financials || {};
  const qual = d.qualification || {};
  const p = d.provenance || {};
  const cat = d.catastale || {};
  const catStr = [cat.foglio && `Fg. ${cat.foglio}`, cat.particella && `Part. ${cat.particella}`, cat.subalterno && `Sub ${cat.subalterno}`].filter(Boolean).join(" · ") || "—";
  const variazioni = (d.variazioni || []).slice().reverse();
  const qualified = qual.esito === "QUALIFICATA";

  // Timeline unificata: acquisizione, documenti, analisi AI, cronologia stati, variazioni, contatti, note
  const timeline = [];
  if (d.created_at) timeline.push({ t: d.created_at, tipo: "acquisizione", label: `Operazione acquisita da ${d.fonte_portale || "fonte"}` });
  (d.documenti || []).forEach((doc) => {
    if (doc.data_acquisizione) timeline.push({ t: doc.data_acquisizione, tipo: "documento", label: `Documento acquisito: ${doc.tipo || doc.nome}` });
  });
  if (d.ai_extracted && d.created_at) timeline.push({ t: d.updated_at || d.created_at, tipo: "ai", label: "Analisi AI eseguita (perizia/avviso)" });
  (d.cronologia || []).forEach((c) => timeline.push({ t: c.timestamp, tipo: "stato", label: `${c.motivo || "Stato"} → ${c.stato}`, user: c.user }));
  (d.variazioni || []).forEach((v) => {
    const isPrice = /prezzo|offerta|valore/i.test(v.campo || "");
    const isDate = /data asta|data/i.test(v.campo || "");
    timeline.push({ t: v.timestamp, tipo: isPrice ? "prezzo" : isDate ? "data" : "variazione", label: `${v.campo}: ${v.da ?? "—"} → ${v.a ?? "—"}`, user: v.fonte });
  });
  (d.contatti || []).forEach((c) => {
    if (c.data_contatto) timeline.push({ t: c.data_contatto, tipo: "contatto", label: `Contatto ${c.tipo || ""}: ${c.nome || ""}`.trim() });
  });
  (d.note_operatore || []).forEach((n) => timeline.push({ t: n.timestamp, tipo: "nota", label: n.testo, user: n.user }));
  timeline.sort((a, b) => new Date(b.t) - new Date(a.t));
  const radarData = Object.entries(d.score?.breakdown || {}).map(([k, v]) => ({ criterio: CRIT_LABELS[k], valore: Math.round((v / WEIGHTS[k]) * 100) }));

  const changeState = async (stato) => {
    setStateOpen(false);
    try {
      const { data } = await api.patch(`/deals/${id}/status`, { stato, motivo: "Aggiornamento manuale" });
      setD(data);
      toast.success(`Stato aggiornato: ${stato}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const genReport = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post(`/deals/${id}/report`);
      setD((prev) => ({ ...prev, ai_report: data }));
      toast.success("Report generato da Claude Sonnet 4.6");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Errore generazione report"); }
    finally { setGenLoading(false); }
  };

  const enrich = async () => {
    setEnrichLoading(true);
    try {
      const { data } = await api.post(`/deals/${id}/enrich`);
      setD(data);
      toast.success("Dati arricchiti con OpenAPI");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Errore arricchimento OpenAPI"); }
    finally { setEnrichLoading(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/deals/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      load();
    } catch { toast.error("Errore caricamento"); }
  };

  const downloadDoc = async (idx, nome) => {
    try {
      const res = await api.get(`/deals/${id}/documenti/${idx}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome || "documento.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "File non disponibile");
    }
  };

  const r = d.ai_report;

  return (
    <div className="space-y-6" data-testid="deal-detail-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <button onClick={() => navigate("/operazioni")} data-testid="back-btn" className="mt-1 w-9 h-9 flex items-center justify-center border border-white/10 rounded-sm text-zinc-400 hover:bg-white/5">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-light text-white break-words">{d.indirizzo}</h1>
              {d.is_demo && <DemoBadge />}
              <span className="text-xs text-[#EA7B2B] border border-[#EA7B2B]/30 bg-[#EA7B2B]/10 px-2 py-0.5 rounded-sm uppercase tracking-wider">{d.strategia}</span>
              {d.multi_lotto && (
                <span data-testid="multilotto-badge" className="text-xs text-[#8B5CF6] border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 px-2 py-0.5 rounded-sm uppercase tracking-wider">
                  Multi-lotto · Lotto {d.lotto_label}/{d.totale_lotti}
                </span>
              )}
            </div>
            <p className="text-sm text-zinc-500 mt-1 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {d.comune} ({d.provincia}) · {d.tribunale} · RGE {d.rge} · Lotto {d.lotto}
            </p>
            {d.link_originale && (
              <a
                href={d.link_originale}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="auction-link"
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#EA7B2B] hover:text-[#F59E4A] border border-[#EA7B2B]/30 bg-[#EA7B2B]/5 px-3 py-1.5 rounded-sm transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Apri annuncio dell'asta · {d.fonte_portale}
              </a>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {d.priority?.level && (
            <div className="text-center px-4 py-2 border rounded-sm" data-testid="priority-box" style={{ borderColor: `${PRIORITY[d.priority.level].color}4d`, backgroundColor: `${PRIORITY[d.priority.level].color}14` }} title={d.priority.giorni_asta != null ? `Asta tra ${d.priority.giorni_asta} giorni` : ""}>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Priorità</div>
              <div className="font-mono text-lg flex items-center gap-1.5 justify-center" style={{ color: PRIORITY[d.priority.level].color }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY[d.priority.level].color }} />
                {PRIORITY[d.priority.level].short}
              </div>
            </div>
          )}
          <div className="text-center px-4 py-2 border border-white/10 rounded-sm bg-[#122A3F]" title="Score di investimento: quanto è interessante l'affare">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Score inv.</div>
            <div className="font-mono text-2xl" style={{ color: scoreColor(d.score?.total) }}>{d.score?.total}</div>
          </div>
          <div className="text-center px-4 py-2 border border-white/10 rounded-sm bg-[#122A3F]" data-testid="confidence-box" title="Confidence Score: quanto sono affidabili e completi i dati raccolti">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Confidence</div>
            <div className="font-mono text-2xl" style={{ color: confColor(d.confidence?.total) }}>{d.confidence?.total ?? "—"}</div>
          </div>
          <div className="text-center px-4 py-2 border border-white/10 rounded-sm bg-[#122A3F] min-w-[92px]" data-testid="completeness-box" title="Completezza dati operazione">
            <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Completezza</div>
            <div className="font-mono text-lg text-zinc-200">{d.completeness?.total ?? 0}%</div>
            <div className="h-1 bg-white/10 rounded-full mt-1 overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${d.completeness?.total ?? 0}%`, backgroundColor: confColor(d.completeness?.total) }} />
            </div>
          </div>
          {/* Workflow */}
          <div className="relative">
            <button onClick={() => setStateOpen((o) => !o)} data-testid="workflow-state-btn" className="flex items-center gap-2 px-4 py-2.5 border rounded-sm text-sm" style={{ color: STATE_COLORS[d.stato], borderColor: `${STATE_COLORS[d.stato]}4d`, backgroundColor: `${STATE_COLORS[d.stato]}14` }}>
              {d.stato} <ChevronDown className="w-4 h-4" />
            </button>
            {stateOpen && (
              <div className="absolute right-0 mt-2 w-56 max-h-72 overflow-y-auto bg-[#122A3F] border border-white/10 rounded-sm z-30 shadow-2xl" data-testid="workflow-menu">
                {WORKFLOW.map((s) => (
                  <button key={s} onClick={() => changeState(s)} className="w-full text-left px-4 py-2 text-sm text-zinc-300 hover:bg-white/5 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATE_COLORS[s] }} /> {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={enrich} disabled={enrichLoading || d.is_demo} data-testid="enrich-btn" title={d.is_demo ? "Non disponibile sulle operazioni demo" : ""} className="flex items-center gap-2 px-4 py-2.5 bg-[#8B5CF6] text-white rounded-sm text-sm font-medium hover:bg-[#7C4DE0] transition-colors disabled:opacity-60">
            {enrichLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Arricchisci dati
          </button>
        </div>
      </div>

      {/* Qualification stamp */}
      <div className={`flex items-center gap-3 px-5 py-3 rounded-sm border ${qualified ? "border-[#10B981]/30 bg-[#10B981]/10" : "border-[#E11D48]/30 bg-[#E11D48]/10"}`} data-testid="qualification-stamp">
        {qualified ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> : <XCircle className="w-5 h-5 text-[#E11D48]" />}
        <span className={`font-display text-lg ${qualified ? "text-[#10B981]" : "text-[#E11D48]"}`}>{qual.esito}</span>
        {!qualified && qual.motivi_scarto?.length > 0 && (
          <span className="text-sm text-zinc-400">· Criteri non rispettati: {qual.motivi_scarto.join(", ")}</span>
        )}
      </div>

      {/* Requisiti operazione — in evidenza, letti dalla perizia */}
      {d.requisiti?.items?.length > 0 && (
        <div className="rounded-sm border-2 border-[#EA7B2B]/50 bg-[#EA7B2B]/[0.06] overflow-hidden" data-testid="requisiti-operazione">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#EA7B2B]/20 bg-[#EA7B2B]/10">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-[#EA7B2B]" />
              <h2 className="text-sm font-semibold text-white uppercase tracking-wider">Requisiti operazione</h2>
              <span className="text-[11px] text-zinc-400 normal-case tracking-normal">· letti dalla perizia</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="text-[#10B981] font-medium">{d.requisiti.soddisfatti} ok</span>
              {d.requisiti.non_soddisfatti > 0 && <span className="text-[#E11D48] font-medium">· {d.requisiti.non_soddisfatti} non soddisfatti</span>}
              {d.requisiti.da_verificare > 0 && <span className="text-[#D97706] font-medium">· {d.requisiti.da_verificare} da verificare</span>}
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {d.requisiti.items.map((r) => {
              const cfg = r.esito === "ok"
                ? { color: "#10B981", Icon: CheckCircle2, label: "OK" }
                : r.esito === "ko"
                ? { color: "#E11D48", Icon: XCircle, label: "NON SODDISFATTO" }
                : { color: "#D97706", Icon: AlertTriangle, label: "DA VERIFICARE" };
              const Icon = cfg.Icon;
              return (
                <div key={r.chiave} className="flex items-start gap-3 px-5 py-3" data-testid={`requisito-${r.chiave}`} title={r.dettaglio}>
                  <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color: cfg.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-white font-medium">{r.nome}</span>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm" style={{ color: cfg.color, backgroundColor: `${cfg.color}1a`, border: `1px solid ${cfg.color}4d` }}>{cfg.label}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5">{r.valore}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(d.data_incompleta || d.note) && (
        <div className="flex items-start gap-3 px-5 py-3 rounded-sm border border-[#D97706]/30 bg-[#D97706]/10" data-testid="deal-note">
          <AlertTriangle className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
          <div className="text-sm">
            {d.data_incompleta && <span className="text-[#D97706] font-medium">Dati da verificare · </span>}
            <span className="text-zinc-300">{d.note || "Alcuni dati economici non sono disponibili dalla fonte e richiedono verifica manuale."}</span>
          </div>
        </div>
      )}

      {d.cross_check?.discrepanze > 0 && (
        <div className="flex items-start gap-3 px-5 py-3 rounded-sm border border-[#E11D48]/30 bg-[#E11D48]/10" data-testid="crosscheck-banner">
          <AlertTriangle className="w-5 h-5 text-[#E11D48] shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="text-[#E11D48] font-medium">Discrepanza tra fonti · </span>
            <span className="text-zinc-300">
              {d.cross_check.discrepanze} {d.cross_check.discrepanze === 1 ? "campo non coincide" : "campi non coincidono"} tra Portale e Documenti: {d.cross_check.flags.join(", ")}. Verifica manuale consigliata.
            </span>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 text-xs text-zinc-500" data-testid="prov-legend">
        <span className="uppercase tracking-wider">Affidabilità dati:</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#10B981" }} /> Certo</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#D97706" }} /> Dedotto</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#E11D48" }} /> Da verificare</span>
        <span className="text-zinc-600">· passa sul pallino per la fonte</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column */}
        <div className="space-y-4">
          <Section title="Immobile" testid="section-immobile">
            <Row label="Tipologia" value={d.tipologia} mono={false} prov={p.tipologia} />
            <Row label="Categoria catastale" value={d.categoria_catastale} />
            <Row label="Superficie" value={`${num(d.superficie)} mq`} prov={p.superficie} />
            <Row label="Prezzo base" value={eur(d.prezzo_base)} prov={p.prezzo_base} accent="#fff" />
            <Row label="Valore perizia" value={eur(d.valore_perizia)} prov={p.valore_perizia} />
            <Row label="Comune" value={`${d.comune || "—"} (${d.provincia || "—"})`} mono={false} prov={p.comune} />
            <Row label="Dati catastali" value={catStr} mono={false} />
            <Row label="Piano" value={d.piano} mono={false} />
            <Row label="Occupazione" value={OCC_LABELS[d.stato_occupazionale] || d.stato_occupazionale} mono={false} prov={p.stato_occupazionale} accent={d.stato_occupazionale === "libero" || d.stato_occupazionale === "esecutato" ? "#10B981" : "#D97706"} />
            <Row label="Abusi" value={d.abusi_presenti ? `Sì · reg. ${eur(d.costi_regolarizzazione)}` : "No"} mono={false} prov={p.abusi_presenti} accent={d.abusi_presenti ? "#D97706" : "#10B981"} />
          </Section>

          <Section title="Posizione debitoria" testid="section-debiti">
            <Row label="Mutui / Ipoteca" value={eur(d.mutui)} />
            <Row label="Pignoramenti" value={d.pignoramenti} />
            <Row label="N. creditori" value={d.numero_creditori} />
            <Row label="Totale debiti" value={eur(d.totale_debiti)} />
            <Row label="Piena proprietà esecutato" value={d.piena_proprieta_esecutato ? "Sì" : "No"} mono={false} />
            <Row label="Spese condominiali" value={eur(d.spese_condominiali)} />
          </Section>

          <Section title="Procedura" testid="section-procedura">
            <Row label="Giudice" value={d.giudice} mono={false} />
            <Row label="Delegato" value={d.delegato} mono={false} />
            <Row label="Custode" value={d.custode} mono={false} />
            <Row label="N. esperimento" value={d.numero_esperimento} />
            <Row label="Data asta" value={dateIt(d.data_asta)} mono={false} />
            <Row label="Fonte" value={d.fonte_portale} mono={false} accent="#EA7B2B" />
          </Section>

          <Section
            title={`Verifica incrociata fonti${d.cross_check?.concordanze != null ? ` · ${d.cross_check.concordanze}✓ ${d.cross_check.discrepanze}⚠` : ""}`}
            testid="section-crosscheck"
          >
            {(d.cross_check?.fields || []).length ? (
              <CrossCheck cc={d.cross_check} />
            ) : (
              <p className="text-xs text-zinc-500">Nessun confronto disponibile (fonte o documenti mancanti).</p>
            )}
          </Section>

          {(d.fonti || []).length > 1 && (
            <Section
              title={`Confronto tra portali · ${d.fonti.length} fonti`}
              testid="section-crossportali"
            >
              <div className="flex flex-wrap gap-2 mb-3" data-testid="fonti-list">
                {d.fonti.map((f, i) => (
                  <a key={i} href={f.link} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-[#8B5CF6] border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 px-2.5 py-1 rounded-sm hover:bg-[#8B5CF6]/20 flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3" /> {f.portale}
                  </a>
                ))}
              </div>
              {(d.cross_portali?.fields || []).length ? (
                <CrossCheck cc={d.cross_portali} cols={`${d.fonti[0]?.portale} · ${d.fonti[1]?.portale}`} />
              ) : (
                <p className="text-xs text-zinc-500">Nessun campo confrontabile tra i portali.</p>
              )}
            </Section>
          )}

          <Section title={`Timeline operazione${timeline.length ? ` (${timeline.length})` : ""}`} testid="section-timeline">
            {timeline.length === 0 && <p className="text-xs text-zinc-500">Nessun evento registrato.</p>}
            <div className="relative pl-4 space-y-3 before:content-[''] before:absolute before:left-[3px] before:top-1 before:bottom-1 before:w-px before:bg-white/10">
              {timeline.map((ev, i) => {
                const c = TL_COLORS[ev.tipo] || "#71717A";
                return (
                  <div key={i} className="relative flex items-start gap-3" data-testid={`timeline-event-${i}`}>
                    <div className="absolute -left-4 top-1 w-2 h-2 rounded-full border-2 border-[#122A3F]" style={{ backgroundColor: c }} />
                    <div className="flex-1">
                      <div className="text-sm text-zinc-200">{ev.label}</div>
                      <div className="text-[10px] text-zinc-600 uppercase tracking-wider">{TL_LABELS[ev.tipo] || ev.tipo}{ev.user ? ` · ${ev.user}` : ""} · {dateTimeIt(ev.t)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>

          <Section title={`Storico variazioni${variazioni.length ? ` (${variazioni.length})` : ""}`} testid="section-variazioni">
            {variazioni.length === 0 && <p className="text-xs text-zinc-500">Nessuna variazione registrata.</p>}
            <div className="space-y-2">
              {variazioni.map((v, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0 text-sm">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#EA7B2B] mt-1.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-zinc-200">{v.campo}: <span className="text-zinc-500 line-through">{String(v.da ?? "—")}</span> → <span className="text-white">{String(v.a ?? "—")}</span></div>
                    <div className="text-[10px] text-zinc-600">{v.fonte} · {dateIt(v.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Center column */}
        <div className="space-y-4">
          <Section title="Scoring Engine · 8 criteri" testid="section-scoring">
            <ResponsiveContainer width="100%" height={260}>
              <RadarChart data={radarData} outerRadius={90}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="criterio" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="valore" stroke="#EA7B2B" fill="#EA7B2B" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
            <div className="grid grid-cols-2 gap-x-4 mt-2">
              {Object.entries(d.score?.breakdown || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-xs py-0.5">
                  <span className="text-zinc-500">{CRIT_LABELS[k]}</span>
                  <span className="font-mono text-zinc-300">{v}/{WEIGHTS[k]}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Analisi economico-finanziaria" testid="section-financials">
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[["ROI", pct(fin.roi), fin.roi > 20 ? "#10B981" : "#D97706"], ["ROE", pct(fin.roe), "#EA7B2B"], ["IRR", pct(fin.irr), "#EA7B2B"]].map(([l, v, c]) => (
                <div key={l} className="text-center border border-white/10 rounded-sm py-3">
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{l}</div>
                  <div className="font-mono text-xl mt-1" style={{ color: c }}>{v}</div>
                </div>
              ))}
            </div>
            <Row label="Sconto su OMI" value={pct(fin.sconto_omi)} accent="#10B981" />
            <Row label="Sconto su perizia" value={pct(fin.sconto_perizia)} accent="#10B981" />
            <Row label="Costo acquisto" value={eur(fin.costo_acquisto)} />
            <Row label="Imposte + notaio" value={eur((fin.imposte || 0) + (fin.notaio || 0))} />
            <Row label="Ristrutturazione" value={eur(fin.costo_ristrutturazione)} />
            <Row label="Liberazione" value={eur(fin.costo_liberazione)} />
            <Row label="Holding cost" value={eur(fin.holding_cost)} />
            <Row label="Totale investimento" value={eur(fin.totale_investimento)} accent="#fff" />
            <Row label="Margine netto" value={eur(fin.margine_netto)} accent="#10B981" />
            <Row label="Prezzo massimo (ROI target)" value={eur(fin.prezzo_massimo)} accent="#EA7B2B" />
          </Section>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {d.openapi && (
            <Section title="Valutazione esterna" testid="section-openapi">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/30" data-testid="openapi-verified-badge">
                  <CheckCircle2 className="w-3 h-3" /> Valutazione esterna verificata
                </span>
                {d.openapi.ambiente === "sandbox" && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30" data-testid="openapi-sandbox-badge">
                    SANDBOX · dati simulati
                  </span>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-3">
                {[["Min", d.openapi.quotazione?.min], ["Medio", d.openapi.quotazione?.med], ["Max", d.openapi.quotazione?.max]].map(([l, v]) => (
                  <div key={l} className="bg-[#0A1826] border border-white/10 rounded-sm py-2">
                    <div className="text-[10px] text-zinc-500 uppercase">{l} €/mq</div>
                    <div className="font-mono text-sm text-white">{v != null ? num(v) : "—"}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-1.5 text-sm">
                <Row label="Valore stimato totale" value={d.openapi.quotazione?.valore_totale ? eur(d.openapi.quotazione.valore_totale) : "—"} />
                <Row label="Scostamento vs perizia" value={d.openapi.scostamento_perizia != null ? `${d.openapi.scostamento_perizia > 0 ? "+" : ""}${d.openapi.scostamento_perizia}%` : "—"} />
                <Row label="Scostamento vs prezzo base" value={d.openapi.scostamento_base != null ? `${d.openapi.scostamento_base > 0 ? "+" : ""}${d.openapi.scostamento_base}%` : "—"} />
                <Row label="Comparabili (transazioni)" value={d.openapi.comparabili?.n_transazioni ?? "—"} />
                <Row label="Prezzo medio transato €/mq" value={d.openapi.comparabili?.prezzo_medio_mq ? num(d.openapi.comparabili.prezzo_medio_mq) : "—"} />
              </div>
              <div className="text-[10px] text-zinc-600 mt-3 pt-2 border-t border-white/10">
                Fonte: {d.openapi.fonte} · {dateTimeIt(d.openapi.data)} · benchmark indipendente, non sostituisce la perizia
              </div>
            </Section>
          )}

          {d.openapi?.zona && (
            <Section title="Attrattività e liquidità della zona" testid="section-zona">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/30" data-testid="zona-sandbox-badge">
                  SANDBOX · dati simulati
                </span>
                <span className="text-[10px] text-zinc-500">indicatori descrittivi · non incidono su Score/ROI</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                {[
                  ["Liquidità", d.openapi.zona.liquidita, "zona-liquidita"],
                  ["Attrattività", d.openapi.zona.attrattivita, "zona-attrattivita"],
                  ["Rischio territoriale", d.openapi.zona.rischio_territoriale, "zona-rischio"],
                ].map(([label, ind, tid]) => {
                  const isRisk = tid === "zona-rischio";
                  const lvl = ind?.livello;
                  const color = !lvl ? "#71717a"
                    : isRisk
                      ? (lvl === "Alto" ? "#EF4444" : lvl === "Medio" ? "#F59E0B" : "#10B981")
                      : (lvl === "Alta" ? "#10B981" : lvl === "Media" ? "#F59E0B" : "#EF4444");
                  return (
                    <div key={tid} className="bg-[#0A1826] border border-white/10 rounded-sm py-2 px-2 text-center" data-testid={tid}>
                      <div className="text-[10px] text-zinc-500 uppercase leading-tight">{label}</div>
                      <div className="font-semibold text-sm mt-1" style={{ color }}>{lvl || "—"}</div>
                      <div className="font-mono text-[10px] text-zinc-500">{ind?.valore != null ? `${ind.valore}/100` : "n/d"}</div>
                    </div>
                  );
                })}
              </div>
              <div className="space-y-1.5 text-sm">
                <Row label="Tempo medio di vendita della zona" value={d.openapi.zona.dettaglio?.tempo_medio_vendita_zona_giorni != null ? `${d.openapi.zona.dettaglio.tempo_medio_vendita_zona_giorni} gg` : "—"} />
                <Row label="Tempo stimato di liquidazione asta" value={d.tempo_liquidazione_mesi ? `${d.tempo_liquidazione_mesi} mesi` : "—"} />
                <Row label="Popolazione comune" value={d.openapi.zona.dettaglio?.popolazione != null ? num(d.openapi.zona.dettaglio.popolazione) : "—"} />
                <Row label="Densità abitativa (ab/kmq)" value={d.openapi.zona.dettaglio?.densita_ab_kmq != null ? num(d.openapi.zona.dettaglio.densita_ab_kmq) : "—"} />
                <Row label="Rischio sismico (zona)" value={d.openapi.zona.dettaglio?.rischio_sismico ?? "—"} />
                <Row label="Prezzo medio transato €/mq" value={d.openapi.zona.dettaglio?.prezzo_medio_transato_mq ? num(d.openapi.zona.dettaglio.prezzo_medio_transato_mq) : "—"} />
              </div>
              <div className="text-[10px] text-zinc-600 mt-3 pt-2 border-t border-white/10">
                Indicatori descrittivi della zona (benchmark). Il tempo medio di vendita della zona è distinto dal tempo di liquidazione dell'asta e non influenza le valutazioni economiche del deal.
              </div>
            </Section>
          )}

          <DueDiligencePanel dealId={id} isDemo={d.is_demo} />

          <Section title="CRM · Gestione operazione" testid="section-crm">
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Operatore assegnato</label>
                <select
                  value={d.crm?.operatore_id || ""}
                  onChange={(e) => saveCrm({ operatore_id: e.target.value })}
                  data-testid="crm-operatore"
                  className="w-full mt-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]"
                >
                  <option value="">Non assegnato</option>
                  {operatori.filter((o) => o.attivo).map((o) => <option key={o.id} value={o.id}>{o.nome} · {o.qualifica}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Prossimo contatto</label>
                  <input type="date" value={(d.crm?.prossimo_contatto || "").slice(0, 10)} onChange={(e) => saveCrm({ prossimo_contatto: e.target.value })} data-testid="crm-prossimo-contatto" className="w-full mt-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider">Stato trattativa</label>
                  <select value={d.crm?.stato_trattativa || ""} onChange={(e) => saveCrm({ stato_trattativa: e.target.value })} data-testid="crm-stato-trattativa" className="w-full mt-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]">
                    <option value="">—</option>
                    {["Da contattare", "In corso", "Offerta presentata", "Vinta", "Persa", "Sospesa"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              {/* Attività */}
              <div className="pt-2 border-t border-white/10">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Attività aperte</div>
                <div className="space-y-1.5" data-testid="crm-attivita-list">
                  {(d.crm?.attivita || []).length === 0 && <p className="text-xs text-zinc-600">Nessuna attività.</p>}
                  {(d.crm?.attivita || []).map((a) => (
                    <div key={a.id} className="flex items-start gap-2" data-testid={`crm-attivita-${a.id}`}>
                      <input type="checkbox" checked={a.stato === "chiusa"} onChange={() => toggleAttivita(a.id)} className="mt-1 accent-[#EA7B2B]" data-testid={`crm-attivita-toggle-${a.id}`} />
                      <div className="flex-1">
                        <div className={`text-sm ${a.stato === "chiusa" ? "line-through text-zinc-600" : "text-zinc-200"}`}>{a.testo}</div>
                        {a.scadenza && <div className="text-[10px] text-zinc-500">Scadenza {a.scadenza}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <input value={newAtt.testo} onChange={(e) => setNewAtt((s) => ({ ...s, testo: e.target.value }))} placeholder="Nuova attività…" data-testid="crm-attivita-input" className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
                  <input type="date" value={newAtt.scadenza} onChange={(e) => setNewAtt((s) => ({ ...s, scadenza: e.target.value }))} data-testid="crm-attivita-date" className="bg-[#0A1826] border border-white/10 rounded-sm px-2 py-2 text-xs text-white focus:outline-none" />
                  <button onClick={addAttivita} data-testid="crm-attivita-add" className="bg-[#EA7B2B] text-black px-3 rounded-sm hover:bg-[#F59E4A] transition-colors"><Plus className="w-4 h-4" /></button>
                </div>
              </div>

              {/* Note condivise */}
              <div className="pt-2 border-t border-white/10">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Note condivise</div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="crm-note-list">
                  {(d.crm?.note_condivise || []).length === 0 && <p className="text-xs text-zinc-600">Nessuna nota.</p>}
                  {(d.crm?.note_condivise || []).slice().reverse().map((n) => (
                    <div key={n.id} className="text-sm border-b border-white/5 pb-1.5">
                      <div className="text-zinc-200">{n.testo}</div>
                      <div className="text-[10px] text-zinc-600">{n.user} · {dateTimeIt(n.timestamp)}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <input value={newNota} onChange={(e) => setNewNota(e.target.value)} placeholder="Aggiungi nota…" data-testid="crm-nota-input" className="flex-1 bg-[#0A1826] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]" />
                  <button onClick={addNota} data-testid="crm-nota-add" className="bg-[#EA7B2B] text-black px-3 rounded-sm hover:bg-[#F59E4A] transition-colors"><Plus className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Motore di qualificazione" testid="section-qualification">
            <div className="space-y-2">
              {(qual.criteri || []).map((c, i) => (
                <div key={i} className="flex items-start gap-2 py-1.5 border-b border-white/5 last:border-0">
                  {c.esito ? <CheckCircle2 className="w-4 h-4 text-[#10B981] shrink-0 mt-0.5" /> : <XCircle className={`w-4 h-4 shrink-0 mt-0.5 ${c.tipo === "bloccante" ? "text-[#E11D48]" : "text-zinc-600"}`} />}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-zinc-200">{c.nome}</span>
                      {c.tipo === "bloccante" && <span className="text-[9px] text-[#E11D48] uppercase tracking-wider">bloccante</span>}
                    </div>
                    <div className="text-xs text-zinc-500">{c.dettaglio}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section
            title="AI Assistant · Report deal"
            testid="section-ai-report"
            action={
              <button onClick={genReport} disabled={genLoading} data-testid="generate-report-btn" className="flex items-center gap-1.5 text-xs bg-[#EA7B2B] text-black px-3 py-1.5 rounded-sm hover:bg-[#F59E4A] disabled:opacity-60">
                {genLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {r ? "Rigenera" : "Genera"}
              </button>
            }
          >
            {!r && !genLoading && <p className="text-sm text-zinc-500 py-6 text-center">Nessun report generato. Usa l'AI Assistant per un executive summary.</p>}
            {genLoading && !r && <div className="py-8 text-center"><Loader2 className="w-6 h-6 text-[#EA7B2B] animate-spin mx-auto" /><p className="text-xs text-zinc-500 mt-2">Claude sta redigendo il report…</p></div>}
            {r && (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-xs text-[#EA7B2B] uppercase tracking-wider mb-1">Executive Summary</div>
                  <p className="text-zinc-300 leading-relaxed">{r.executive_summary}</p>
                </div>
                {r.analisi_economica && (
                  <div>
                    <div className="text-xs text-[#EA7B2B] uppercase tracking-wider mb-1">Analisi economica</div>
                    <p className="text-zinc-300 leading-relaxed">{r.analisi_economica}</p>
                  </div>
                )}
                {r.punti_forza?.length > 0 && (
                  <div>
                    <div className="text-xs text-[#10B981] uppercase tracking-wider mb-1">Punti di forza</div>
                    <ul className="list-disc list-inside text-zinc-400 space-y-0.5">{r.punti_forza.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {r.criticita?.length > 0 && (
                  <div>
                    <div className="text-xs text-[#D97706] uppercase tracking-wider mb-1">Criticità</div>
                    <ul className="list-disc list-inside text-zinc-400 space-y-0.5">{r.criticita.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {(r.rischi_legali?.length > 0 || r.rischi_urbanistici?.length > 0) && (
                  <div className="grid grid-cols-1 gap-2">
                    {r.rischi_legali?.length > 0 && <div><div className="text-xs text-[#E11D48] uppercase tracking-wider mb-1">Rischi legali</div><ul className="list-disc list-inside text-zinc-400 space-y-0.5">{r.rischi_legali.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                    {r.rischi_urbanistici?.length > 0 && <div><div className="text-xs text-[#E11D48] uppercase tracking-wider mb-1">Rischi urbanistici</div><ul className="list-disc list-inside text-zinc-400 space-y-0.5">{r.rischi_urbanistici.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/10">
                  <div><div className="text-xs text-zinc-500">Prezzo max consigliato</div><div className="font-mono text-[#EA7B2B]">{eur(r.prezzo_massimo_consigliato)}</div></div>
                  <div><div className="text-xs text-zinc-500">Strategia uscita</div><div className="text-zinc-300 text-xs">{r.strategia_uscita}</div></div>
                </div>
                {r.valutazione_finale && (
                  <div className="bg-[#0A1826] border border-white/10 rounded-sm p-3">
                    <div className="text-xs text-[#EA7B2B] uppercase tracking-wider mb-1">Valutazione finale</div>
                    <p className="text-zinc-300">{r.valutazione_finale}</p>
                  </div>
                )}
              </div>
            )}
          </Section>

          <Section title="Contatti pubblici" testid="section-contacts">
            <div className="space-y-3">
              {(d.contatti || []).map((c, i) => (
                <div key={i} className="border-b border-white/5 last:border-0 pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm text-white">{c.nome}</div>
                    <ContactCompliance dealId={id} index={i} compliance={c.compliance} canEdit={["admin", "analista", "responsabile"].includes(user?.role)} onSaved={load} />
                  </div>
                  <div className="text-xs text-zinc-500">{c.tipo}</div>
                  <div className="flex flex-col gap-0.5 mt-1 text-xs text-zinc-400">
                    {c.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.telefono}</span>}
                    {c.pec && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.pec}</span>}
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-1">{c.fonte} · {c.base_liceita}</div>
                </div>
              ))}
              {(!d.contatti || d.contatti.length === 0) && <p className="text-xs text-zinc-500">Nessun contatto pubblico disponibile.</p>}
            </div>
          </Section>

          <Section
            title={`Documentazione${(d.documenti || []).length ? ` (${d.documenti.length})` : ""}`}
            testid="section-documents"
            action={
              <button onClick={() => fileRef.current?.click()} data-testid="upload-doc-btn" className="flex items-center gap-1.5 text-xs border border-white/10 text-zinc-300 px-3 py-1.5 rounded-sm hover:bg-white/5">
                <Upload className="w-3.5 h-3.5" /> Carica
              </button>
            }
          >
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            <div className="space-y-1.5">
              {(d.documenti || []).map((doc, i) => (
                <button
                  key={i}
                  onClick={() => downloadDoc(i, doc.nome)}
                  data-testid={`download-doc-${i}`}
                  className="w-full flex items-center gap-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5 rounded-sm px-2 py-1.5 transition-colors group"
                >
                  <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
                  <span className="truncate text-left flex-1">{doc.categoria || doc.nome}</span>
                  {doc.size != null && <span className="text-[10px] text-zinc-600 font-mono">{Math.max(1, Math.round(doc.size / 1024))} KB</span>}
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-white/10 text-zinc-500">{doc.tipo}</span>
                  <Download className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#EA7B2B]" />
                </button>
              ))}
              {(!d.documenti || d.documenti.length === 0) && <p className="text-xs text-zinc-500">Nessun documento archiviato.</p>}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}


const COMPLIANCE_OPTS = [
  { v: "verde", c: "#10B981", label: "Dato pubblico verificato" },
  { v: "giallo", c: "#F59E0B", label: "Da verificare" },
  { v: "rosso", c: "#EF4444", label: "Non usare" },
];

function ContactCompliance({ dealId, index, compliance, canEdit, onSaved }) {
  const [open, setOpen] = useState(false);
  const [fonte, setFonte] = useState(compliance?.fonte || "");
  const [base, setBase] = useState(compliance?.base_giuridica || "");
  const [saving, setSaving] = useState(false);
  const cur = COMPLIANCE_OPTS.find((o) => o.v === compliance?.stato);

  const save = async (stato) => {
    setSaving(true);
    try {
      await api.put(`/deals/${dealId}/contatti/compliance`, { index, stato, fonte, base_giuridica: base });
      toast.success("Compliance aggiornata");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative">
      <button
        onClick={() => canEdit && setOpen((o) => !o)}
        data-testid={`compliance-badge-${index}`}
        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-sm border"
        style={{ color: cur?.c || "#71717a", borderColor: (cur?.c || "#71717a") + "55", backgroundColor: (cur?.c || "#71717a") + "12", cursor: canEdit ? "pointer" : "default" }}
        title={cur?.label || "Compliance non impostata"}
      >
        <span style={{ fontSize: 10 }}>●</span> {cur ? cur.v : "n/d"}
      </button>
      {open && canEdit && (
        <div className="absolute right-0 top-6 z-20 w-64 bg-[#0A1826] border border-white/15 rounded-sm p-3 shadow-xl space-y-2" data-testid={`compliance-editor-${index}`}>
          <div className="text-[10px] uppercase text-zinc-500">Base giuridica</div>
          <input value={base} onChange={(e) => setBase(e.target.value)} placeholder="es. dato pubblico / interesse legittimo" data-testid={`compliance-base-${index}`} className="w-full bg-[#122A3F] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white focus:outline-none" />
          <input value={fonte} onChange={(e) => setFonte(e.target.value)} placeholder="fonte (es. avviso di vendita, PVP)" data-testid={`compliance-fonte-${index}`} className="w-full bg-[#122A3F] border border-white/10 rounded-sm px-2 py-1.5 text-xs text-white focus:outline-none" />
          <div className="flex gap-1.5">
            {COMPLIANCE_OPTS.map((o) => (
              <button key={o.v} onClick={() => save(o.v)} disabled={saving} data-testid={`compliance-set-${o.v}-${index}`}
                className="flex-1 text-[10px] py-1.5 rounded-sm border transition-colors"
                style={{ color: o.c, borderColor: o.c + "55", backgroundColor: o.c + "12" }}>
                {o.v}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
